import { NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DosageOption } from '@models/dosage-upgrade/dosage-option';
import { firstValueFrom } from 'rxjs';

import { UpgradeDosageUpsellComponent } from '../upgrade-dosage-upsell.component';

/**
 * GLP-1 specific dosage upgrade upsell component.
 *
 * Loads GLP-1 dosage configuration from JSON and passes it to the
 * UpgradeDosageUpsellComponent for rendering.
 */
@Component({
  selector: 'app-glp1-upgrade-dosage-upsell',
  standalone: true,
  imports: [NgIf, ReactiveFormsModule, UpgradeDosageUpsellComponent],
  templateUrl: './glp1-upgrade-dosage-upsell.component.html',
})
export class Glp1UpgradeDosageUpsellComponent implements OnInit {
  @Input() public testId!: number;
  @ViewChild(UpgradeDosageUpsellComponent, { static: false })
  public upgradeDosageUpsellComponent: UpgradeDosageUpsellComponent;

  public upgradeDosageOptions: DosageOption[] = [];
  public currentDosageData: any;
  public nextDosageData: any;
  public form: FormGroup;

  private http = inject(HttpClient);

  /**
   * Initialize component and load dosage configuration
   */
  public async ngOnInit() {
    try {
      await this.loadDosageConfig();
    } catch (error) {
      this.upgradeDosageOptions = [];
      this.form = null;
    }
  }

  /**
   * Load and build GLP-1 dosage configuration from JSON file
   */
  private async loadDosageConfig(): Promise<void> {
    const dosages: any[] = await firstValueFrom(this.http.get<any[]>('assets/questionnaires/glp-1-dosages.json'));
    this.currentDosageData = this.findCurrentDosage(dosages);

    if (!this.currentDosageData.nextDosage) {
      this.upgradeDosageOptions = [];
      this.form = null;

      return;
    }

    this.nextDosageData = this.findNextDosage(dosages, this.currentDosageData.nextDosage);
    this.upgradeDosageOptions = this.buildUpgradeOption(this.nextDosageData);

    this.form = new FormGroup({
      upgradeDosage: new FormControl('', Validators.required),
    });
  }

  /**
   * Find current dosage data by test ID
   *
   * @param dosages Array of dosage data from JSON
   */
  private findCurrentDosage(dosages: any[]): any {
    const currentDosageData = dosages.find((d: any) => d.id === this.testId);

    if (!currentDosageData) {
      throw new Error('Current dosage not found');
    }

    return currentDosageData;
  }

  /**
   * Find next dosage data by ID
   *
   * @param dosages Array of dosage data from JSON
   * @param nextDosageId ID of the next dosage
   */
  private findNextDosage(dosages: any[], nextDosageId: number): any {
    const nextDosageData = dosages.find((d: any) => d.id === nextDosageId);

    if (!nextDosageData) {
      throw new Error('Next dosage not found');
    }

    return nextDosageData;
  }

  /**
   * Build upgrade option from next dosage data
   *
   * @param nextDosageData Next dosage data
   */
  private buildUpgradeOption(nextDosageData: any): DosageOption[] {
    return [
      {
        value: `${nextDosageData.name} ${nextDosageData.dosage}`,
        label: nextDosageData.dosage,
        price: nextDosageData.price,
      },
    ];
  }
}
